import { cn } from '@/shared/utils/formatting';

const STORE_CONFIG = {
  apple: {
    href: 'https://apps.apple.com/br/app/taboo-tv/id6738045672?l=en-GB',
    sublabel: 'Download on the',
    storeName: 'App Store',
  },
  google: {
    href: 'https://play.google.com/store/apps/details?id=com.tvtaboo&pcampaignid=web_share',
    sublabel: 'GET IT ON',
    storeName: 'Google Play',
  },
} as const satisfies Record<
  'apple' | 'google',
  { href: string; sublabel: string; storeName: string }
>;

export const STORE_URLS = {
  ios: STORE_CONFIG.apple.href,
  android: STORE_CONFIG.google.href,
} as const;

const STORE_KEYS = ['apple', 'google'] as const;

export function AppleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 384 512"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184 4 273.5c0 26.2 4.8 53.3 14.4 81.2 12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-62.1 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

export function GooglePlayIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 512 512"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
    </svg>
  );
}

const ICONS = {
  apple: AppleIcon,
  google: GooglePlayIcon,
} as const;

export function AppStoreBadge({
  store,
  className,
}: {
  store: 'apple' | 'google';
  className?: string;
}) {
  const config = STORE_CONFIG[store];
  const Icon = ICONS[store];

  return (
    <a
      href={config.href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-2 rounded-md border border-white/20 bg-black px-2 py-1 transition-opacity hover:opacity-80',
        className
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-white" />
      <div className="flex flex-col">
        <span className="text-[7px] font-medium uppercase leading-tight tracking-wide text-white/70">
          {config.sublabel}
        </span>
        <span className="text-[10px] font-semibold leading-tight text-white">
          {config.storeName}
        </span>
      </div>
    </a>
  );
}

export function AppStoreBadges({
  className,
  badgeClassName,
}: {
  className?: string;
  badgeClassName?: string;
} = {}) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {STORE_KEYS.map((store) => (
        <AppStoreBadge
          key={store}
          store={store}
          {...(badgeClassName !== undefined ? { className: badgeClassName } : {})}
        />
      ))}
    </div>
  );
}
